/**
 * Store wiring for the karcis session lifecycle.
 *
 * The shapes these write are built by `karcisSession` (pure, tested); this file
 * is only the plumbing that puts them into the session store, logs them, and
 * pushes them to the server. See `karcisSession` for why the karcis booth had
 * no sessions and no activity logs at all before this existed.
 */

import { flushSessionLogs, useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import type { DeepPartial, SessionStateData } from '@renderer/types/sessionState'
import { takeSessionId } from '@renderer/utils/sessionIdSource'
import { triggerSyncNow, uploadSessionState } from '@renderer/utils/pipeline/stateUploader'
import {
  KARCIS_CUSTOM_PATH,
  buildKarcisPayment,
  buildKarcisSessionFinish,
  buildKarcisSessionStart,
  resolveKarcisProjectId,
  type KarcisPaymentParams
} from './karcisSession'

/**
 * Open a new karcis session and push it to the server straight away.
 *
 * The POST has to happen at the START of the cycle, not the end: activity logs
 * are uploaded against a session id and the server keys them to the session
 * document, so it must already exist by the time the first print log flushes.
 * Returns the new session id.
 */
export function startKarcisSession(
  routeProjectId: string | undefined,
  amount: number,
  copies: number = 1
): string {
  const store = useSessionStateStore.getState()
  const sessionId = takeSessionId()
  const projectId = resolveKarcisProjectId(useConfigStore.getState().config?._id, routeProjectId)
  // Set by `initializeSession` (ProjectRoute/View) before this page renders.
  // The booth loops in one component and never calls `cleanupSession`, so it
  // survives every cycle. An empty value is survivable anyway: the server
  // overwrites `userId` with the authenticated account on write.
  const userId = store.userId

  store.setSessionState(
    buildKarcisSessionStart({
      sessionId,
      projectId,
      userId,
      startedAt: new Date(),
      amount,
      filterId: store.userPreferences.filterId,
      frameId: store.userPreferences.frameId,
      copies
    })
  )

  store.logActivity(
    'Karcis session started',
    { customPath: KARCIS_CUSTOM_PATH, projectId, amount, copies },
    'welcome',
    sessionId
  )

  void store.uploadSessionState(sessionId).catch((error) => {
    console.error('[karcisSession] Initial session upload failed:', error)
  })

  return sessionId
}

/**
 * Record a payment state change on the session.
 *
 * `upload` is off by default because an idle booth re-quotes a QR every two
 * minutes forever, and each quote is a new `orderId` — so uploading on every
 * quote would be a POST per QR all night for a booth nobody is standing at.
 * Quotes are worth an activity log (that is what tells you a booth was alive
 * and reaching the gateway); only settlement is worth a session write.
 */
export function recordKarcisPayment(
  sessionId: string,
  params: KarcisPaymentParams,
  options?: { upload?: boolean }
): void {
  if (!sessionId) return
  const store = useSessionStateStore.getState()
  store.setSessionState(buildKarcisPayment(params))
  store.logActivity(
    `Karcis payment ${params.status}`,
    { orderId: params.orderId, amount: params.amount, status: params.status },
    'payment',
    sessionId
  )
  if (options?.upload !== true) return
  void store.uploadSessionState(sessionId).catch((error) => {
    console.error('[karcisSession] Payment upload failed:', error)
  })
}

/**
 * Everything `buildSessionData` reads out of the store, frozen at this instant.
 *
 * The session store is a single slot and the karcis booth reuses it for every
 * customer, so the dying session has to be photographed before the next one
 * moves in: `finishKarcisSession` suspends on its first await, and the reset
 * path opens the next cycle in the very next statement. Without this the final
 * POST for session A would carry session B's timeline and transaction.
 */
function snapshotSession(): DeepPartial<SessionStateData> {
  const s = useSessionStateStore.getState()
  return {
    projectId: s.projectId,
    userId: s.userId,
    eventId: s.eventId,
    timeline: s.timeline,
    userPreferences: s.userPreferences,
    userConsent: s.userConsent,
    devices: s.devices,
    transaction: s.transaction,
    gallery: s.gallery,
    galleryKey: s.galleryKey,
    appSettingsTelemetryId: s.appSettingsTelemetryId
  } as DeepPartial<SessionStateData>
}

/**
 * Close the session: record the outcome, force the final POST through, and
 * drain the activity-log buffer now rather than waiting out the 10s debounce —
 * the next cycle starts within seconds and an unattended booth can be powered
 * off at any point between the two.
 *
 * This is `finalizeSession` with a snapshot, and it goes straight to the
 * uploader rather than through the store's serialized queue: the outgoing and
 * incoming sessions are separate documents keyed by different ids, so they
 * cannot clobber each other, and the outgoing one must not wait behind the
 * next customer's upload.
 */
export async function finishKarcisSession(
  sessionId: string,
  { printed, reason }: { printed: boolean; reason: string }
): Promise<void> {
  if (!sessionId) return
  const store = useSessionStateStore.getState()
  store.setSessionState(
    buildKarcisSessionFinish({
      startedAt: store.timeline.startTime,
      endedAt: new Date(),
      printed
    })
  )
  store.logActivity(
    printed ? 'Karcis session completed' : 'Karcis session ended without a print',
    { customPath: KARCIS_CUSTOM_PATH, printed, reason },
    'result',
    sessionId
  )

  // Taken AFTER the patch above and BEFORE the first await, so it is this
  // session's final state and nothing else's.
  const snapshot = snapshotSession()

  try {
    await uploadSessionState(sessionId, snapshot as Record<string, unknown>, { force: true })
    await triggerSyncNow()
  } catch (error) {
    console.error('[karcisSession] Final session upload failed:', error)
  }

  try {
    await flushSessionLogs(sessionId)
  } catch (error) {
    console.error('[karcisSession] Activity log flush failed:', error)
  }
}
